import type { ExecutionStatus } from './agentExecutionHistory';

export enum FlowExecutionStatus {
  RUNNING = 'RUNNING',
  WAITING_FOR_USER = 'WAITING_FOR_USER',
  /**
   * An AGENT_TASK step called `wait_for_replies` (Slack conversation wait).
   * Distinct from WAITING_FOR_USER (ASK_USER/APPROVAL_GATE) so the ASK_USER/
   * APPROVAL_GATE resolvers and DoBox approval-entry listing — which gate on
   * WAITING_FOR_USER — never surface a reply-wait as something they can act on.
   * Resumed by handleConversationReply.ts, not the ASK_USER/APPROVAL_GATE resolvers.
   */
  WAITING_FOR_REPLY = 'WAITING_FOR_REPLY',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REJECTED = 'REJECTED',
}

export enum PendingDecisionUnreadable {
  PERMANENT = 'PERMANENT',
}

export enum FlowFailureCode {
  PAUSE_EXPIRED = 'PAUSE_EXPIRED',
  CONTINUATION_LOST = 'CONTINUATION_LOST',
  RETRY_EXHAUSTED = 'RETRY_EXHAUSTED',
  ABANDONED_LEGACY = 'ABANDONED_LEGACY',
  FLOW_CHANGED = 'FLOW_CHANGED',
  NESTED_PAUSE = 'NESTED_PAUSE',
  STEP_FAILED = 'STEP_FAILED',
  CONTEXT_UNREADABLE = 'CONTEXT_UNREADABLE',
}

export enum FlowPauseKind {
  ASK_USER = 'ASK_USER',
  APPROVAL = 'APPROVAL',
  REPLY = 'REPLY',
}

export enum FlowResumeKind {
  DECISION = 'DECISION',
  REPLY = 'REPLY',
  REPLY_TIMEOUT = 'REPLY_TIMEOUT',
}

/** Where a sealed engine context lives: on the row (INLINE) or in the context bucket (S3). */
export enum FlowContextForm {
  INLINE = 'INLINE',
  S3 = 'S3',
}

/** How a user pause was settled. APPROVED and REJECTED are ApprovalDecision's values; ANSWERED is an ASK_USER reply. */
export enum FlowDecisionOutcome {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ANSWERED = 'ANSWERED',
}

/** The shape of the sealed side envelope: a data key wrapped by KMS plus the AES-GCM blob it opens. */
export interface FlowDecisionEnvelopeAttribute {
  encryptedDataKey: string;
  sealed: string;
}

export enum FlowTriggerRefKind {
  SINGLE = 'SINGLE',
  DEBOUNCED = 'DEBOUNCED',
  SYNTHETIC = 'SYNTHETIC',
  NONE = 'NONE',
}

/** Which UserCommunications row a run was triggered by; rides inside the sealed variableContext so a resume can reload it. */
export interface FlowTriggerRef {
  kind: FlowTriggerRefKind;
  messageId?: string;
  /** Partition owner of the row; differs from the agent user on team fan-out. */
  ownerUserId?: string;
}

export interface SuspensionRecord {
  ranPrefix?: string;
  stepType: string;
}

export interface FlowExecution {
  executionId: string;
  userId: string;
  flowId: string;
  status: FlowExecutionStatus;
  /** Index of the next step to execute (0-based). */
  cursor: number;
  /** Accumulated step outputs and trigger seed data. In-memory shape used by the
   * runner. Persisted in one of three forms depending on size: small contexts
   * gzipped inline as `variableContextGz`; large contexts offloaded to S3 with
   * only `variableContextS3Key` kept on the row (see FlowExecutionStore). The
   * store picks the form on write and reconstructs `variableContext` on read. */
  variableContext: Record<string, unknown>;
  /** Internal: gzip+base64 of `variableContext` when it fits inline under
   * DynamoDB's per-request limits. Written by FlowExecutionStore; inflated back
   * into `variableContext` by getExecution. Absent on rows written before
   * compression was introduced, and on rows offloaded to S3. */
  variableContextGz?: string;
  /** Internal: S3 object key holding the gzip+base64 `variableContext` when it
   * is too large to embed in a DynamoDB UpdateItem request (compression headroom
   * is finite — see issue #3190). Mutually exclusive with `variableContextGz`:
   * the store always sets exactly one and REMOVEs the other. getExecution only
   * fetches the object when the caller opts in (`hydrateContextFromS3`); the
   * resume resolver does not need the context and so never reads S3. */
  variableContextS3Key?: string;
  /** Internal: the stage (`prod`/`staging`) whose worker created the execution. Its
   *  offloaded context lives in that stage's bucket, so every continuation is
   *  enqueued onto that stage's queue. Absent on rows created before it was stamped. */
  ownerStage?: string;
  /** Internal: wrapped KMS data key sealing `variableContextGz` (inline or S3
   * body) under the AgentMemories CMK. Absent on rows written before context
   * encryption — those are read as legacy plaintext gzip until the backfill
   * runs. Never exposed in GraphQL. */
  encryptedDataKey?: string;
  /** Set when status is WAITING_FOR_USER — the agentId+order of the ASK_USER step. */
  pendingAskUserStepId?: string;
  /** Kind-tagged identity tree of `steps[0..suspendedIndex]` as loaded on suspend (`uid:<JSON>` when every node has a `stepUid`, else `type:<JSON>` from `stepType`; nodes are `[identity, children[]]`); the resume refuses on a mismatch. Cleared when the cursor advances. */
  suspendedRanPrefix?: string;
  /** StepType of the step the run suspended at, for the refusal metric dimension. Cleared when the cursor advances. */
  suspendedStepType?: string;
  /** Populated by setUserDecision; injected as the paused step's output on resume. */
  pendingDecision?: string;
  /** Read-time marker, never persisted: `pendingDecision` exists but can never be opened (the field is then absent). */
  pendingDecisionUnreadable?: PendingDecisionUnreadable;
  /**
   * The `timestampId` of the AgentExecutionHistory record created for the
   * initiating run.  Set when the execution first pauses at an ASK_USER step
   * so that the resume path can update the history record to SUCCESS/FAILURE.
   * Optional — absent on older executions and non-ASK_USER flows.
   */
  historyTimestampId?: string;
  /**
   * The conversationId of the per-execution system conversation created by the
   * ASK_USER strategy to hold the choice card for this execution.
   * Internal field — not exposed in GraphQL.
   * Absent on executions created before this field was added; the resolver
   * falls back to the shared background-tasks conversation in that case.
   */
  askUserConversationId?: string;
  /** The pause step id `askUserConversationId` belongs to: lets a resume tell an ASK_USER wait from a later gate. Readable, an opaque id. */
  askUserStepId?: string;
  /**
   * Checkpoint for a top-level FOR_EACH that yielded mid-loop to re-enqueue
   * itself (so a long loop is paced across invocations instead of blowing the
   * worker's 180s timeout). `stepOrder` is the FOR_EACH step's order and
   * `nextIteration` is the 0-based index of the next iteration to run. Present
   * only while a FOR_EACH is mid-pacing; cleared (REMOVEd) when any step
   * advances the cursor normally. See apps/api/ARCHITECTURE.md › FOR_EACH self-requeue.
   */
  forEachState?: {
    stepOrder: number;
    nextIteration: number;
  };
  startedAt: number;
  updatedAt: number;
  completedAt?: number;
  failureReason?: string;
  /** DynamoDB TTL epoch-seconds; set on terminal states for 30-day cleanup. */
  ttl?: number;
  /**
   * True when this execution is the one-time seed run fired by
   * `triggerInitialSetupExecution` for a template-created `isSetupOnly`
   * agent. Lets a caller answer "does this user have a setup run in flight"
   * without joining back to `UserCommunicationAgents`. Absent on every row
   * written before this field existed — always read as `false`, never as
   * unknown/blocking.
   */
  isSetupExecution?: boolean;
  /** Counts every write; the fence of a conditional write. Readable: the engine branches on it structurally. */
  version?: number;
  /** Stamp of the state-machine engine that owns the row; absent on legacy rows. Readable: the engine branches on it structurally. */
  engineVersion?: number;
  /** Identity of the continuation the row owes or is running. Readable: the engine branches on it structurally. */
  wakeVersion?: number;
  /** Claims of the current wake that did not end in progress. Readable: the engine branches on it structurally. */
  wakeAttempts?: number;
  /** Resends of the current wake by the watchdog. Readable: the engine branches on it structurally. */
  wakeRedrives?: number;
  /** When the current wake became due (epoch ms). Readable: the engine branches on it structurally. */
  wakeSince?: number;
  /** Holder of the lease: requestId:messageId. Readable: the engine branches on it structurally. */
  leaseId?: string;
  /** Lease the last claim displaced, or the NONE sentinel (reclaim metric). Readable: an opaque requestId:messageId pair, the same kind as `leaseId`. */
  priorLeaseId?: string;
  /** Lease the watchdog released from a killed holder, until progress or a clean release (reclaim metric). Readable: an opaque requestId:messageId pair, the same kind as `leaseId`. */
  abandonedLeaseId?: string;
  /** Lease end (epoch ms). Readable: the engine branches on it structurally. */
  leaseUntil?: number;
  /** Key of the watch index: stable hash of the executionId, 0..7. Readable: GSI key of FlowExecutionWatchIndex. */
  watchShard?: number;
  /** Sort key of the watch index: the next time someone must look at the row (epoch ms). Readable: GSI key of FlowExecutionWatchIndex. */
  watchAt?: number;
  /** The `version` of the write that last sealed the context; part of its AAD. Readable: the engine branches on it structurally. */
  contextVersion?: number;
  /** What the row waits for. Readable: the engine branches on it structurally. */
  pauseKind?: FlowPauseKind;
  /** The `wakeVersion` of the segment that paused; binds a decision or reply to its pause. Readable: the engine branches on it structurally. */
  pauseEpoch?: number;
  /** When the pause expires (epoch ms); also the row's `watchAt` while paused. Readable: the engine branches on it structurally. */
  pauseDeadline?: number;
  /** sha256 over the uids or types of the steps that already ran, no user content. Readable: the engine branches on it (FLOW_CHANGED check). */
  ranPrefixDigest?: string;
  /** What the history projection writes for this terminal row (SUCCESS, PARTIAL_FAILURE or FAILURE). Readable: public by construction, a closed enum in code. */
  historyStatus?: ExecutionStatus;
  /** InboxEntries id of the approval card. Readable: DynamoDB key of that row (found by id alone through InboxEntriesById). */
  cardEntryId?: string;
  /** Why the run ended FAILED. Readable: public by construction, a closed enum in code. */
  failureCode?: FlowFailureCode;
  /** Free text about a failure. Sealed under the row key (`FLOW_ENGINE_SEALED_FIELD.FAILURE_DETAIL`): user content. */
  failureDetail?: string;
  /** A non-terminal transition whose history projection failed and is owed to the watchdog. Readable: the engine branches on it structurally. */
  projectPending?: boolean;
  /** Why a DUE row resumes. Readable: the engine branches on it structurally. */
  resumeKind?: FlowResumeKind;
  /** How the pause was settled. Readable: the engine branches on it structurally (a closed enum in code). */
  decisionOutcome?: FlowDecisionOutcome;
  /** The ASK_USER answer, sealed under its own data key (`encryptedDataKey` + `sealed`): user content, so sealed in the PR that introduced it. Absent for an approval, which carries no text. Opened at claim. */
  decisionEnvelope?: FlowDecisionEnvelopeAttribute;
}

export enum ApprovalDecision {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}
